import { stopsAreSameStop } from "@vygruppen/vy-train-map";
import { stopNameWithFallback } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/generateStretchName";
import {
  FormErrorWithDiscriminatedField,
  FormSchema,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/formSchema";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ChoiceChip,
  Field,
  FieldErrorText,
  HStack,
  Stack,
  Text,
} from "shared/components/Spor";
import { range } from "shared/utils/arrayUtils";
import { useStretchBuilder } from "stores/useStretchBuilder";

export const StationSingleStop: FC = () => {
  const { t } = useTranslation();
  const { watch, setValue, formState, clearErrors } =
    useFormContext<FormSchema>();
  const selectedTracks = watch("stationForm.affectedTracks");
  const selectedStop = watch("stationForm.stop");
  const selectedStops = useStretchBuilder((state) => state.selectedStops);

  // Nested errors within discriminated types aren't great to work with in
  // react-hook-form – add error types for each layer below discriminator
  const fieldErrors: FormErrorWithDiscriminatedField<
    "stop" | "affectedTracks"
  > = formState.errors.stationForm;

  useEffect(() => {
    if (selectedStops.length === 1) {
      setValue("stationForm.stop", selectedStops[0]);
    }
  }, []);

  const toggleCheckedTracks = (value: number) => {
    if (selectedTracks === undefined) {
      setValue("stationForm.affectedTracks", [value]);
    } else if (selectedTracks.includes(value)) {
      setValue(
        "stationForm.affectedTracks",
        selectedTracks.filter((track) => track !== value),
      );
    } else {
      setValue("stationForm.affectedTracks", [...selectedTracks, value]);
    }
  };

  return (
    <Stack>
      <Field as="legend">{t("Stasjon")}</Field>
      <HStack flexWrap="wrap">
        {selectedStops.map((stop) => (
          <ChoiceChip
            key={`${stop.countryCode}-${stop.name}`}
            checked={
              selectedStop ? stopsAreSameStop(stop, selectedStop) : false
            }
            onCheckedChange={() => {
              setValue("stationForm.stop", stop);
              clearErrors("stationForm.stop");
            }}
          >
            {stopNameWithFallback(stop)}
          </ChoiceChip>
        ))}
      </HStack>
      <Field invalid={!!fieldErrors?.stop}>
        <FieldErrorText marginTop="12px">
          {fieldErrors?.stop?.message}
        </FieldErrorText>
      </Field>
      <Field as="legend">{t("Spor")}</Field>
      <HStack flexWrap="wrap">
        {range(19)
          .map((it) => it + 1)
          .map((track) => (
            <ChoiceChip
              key={track}
              checked={selectedTracks?.includes(track) ?? false}
              onCheckedChange={() => {
                toggleCheckedTracks(track);
                clearErrors("stationForm.affectedTracks");
              }}
            >
              <Text w="10px" display="flex" justifyContent="center">
                {track}
              </Text>
            </ChoiceChip>
          ))}
      </HStack>
      <Field invalid={!!fieldErrors?.affectedTracks}>
        <FieldErrorText marginTop="12px">
          {fieldErrors?.affectedTracks?.message}
        </FieldErrorText>
      </Field>
    </Stack>
  );
};
